import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import {
    Dialog,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from '@/components/ui/dialog';
import { Plus, Trash2, ShieldOff } from 'lucide-react';
import { apiClient } from '@/api/client';
import { useScope, brandColorVar } from '@/context/ScopeContext';
import TESTIDS from '@/constants/testIds';
import { toast } from 'sonner';

export default function Accounts() {
    const { brands, accounts, refresh } = useScope();
    const [open, setOpen] = useState(false);
    const [brandId, setBrandId] = useState('');
    const [handle, setHandle] = useState('');
    const [displayName, setDisplayName] = useState('');
    const [busy, setBusy] = useState(false);

    const create = async () => {
        if (!brandId || !handle.trim()) {
            toast.error('Pick a brand and provide a handle');
            return;
        }
        setBusy(true);
        try {
            await apiClient.post('/accounts', {
                brand_id: brandId,
                platform: 'instagram',
                handle: handle.trim(),
                display_name: displayName.trim() || undefined,
            });
            await refresh();
            setOpen(false);
            setHandle('');
            setDisplayName('');
            toast.success('Account connected (placeholder)');
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Failed to connect account');
        } finally {
            setBusy(false);
        }
    };

    const remove = async (id) => {
        try {
            await apiClient.delete(`/accounts/${id}`);
            await refresh();
            toast.success('Account disconnected');
        } catch (e) {
            toast.error('Failed to disconnect');
        }
    };

    const toggleStop = async (a) => {
        try {
            await apiClient.post('/automation/emergency_stop', {
                scope: 'account',
                scope_id: a.id,
                enabled: !a.emergency_stop,
            });
            await refresh();
            toast.success(`Emergency stop ${!a.emergency_stop ? 'engaged' : 'lifted'} for @${a.handle}`);
        } catch (e) {
            toast.error('Failed to toggle stop');
        }
    };

    return (
        <div className="space-y-4">
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-2xl font-semibold tracking-tight">Connected accounts</h1>
                    <p className="text-sm text-muted-foreground">
                        Multiple Instagram accounts per brand. Real Meta credentials plug into the same connection records.
                    </p>
                </div>
                <Dialog open={open} onOpenChange={setOpen}>
                    <DialogTrigger asChild>
                        <Button data-testid={TESTIDS.accountsConnectButton}>
                            <Plus className="mr-1 h-4 w-4" /> Connect account
                        </Button>
                    </DialogTrigger>
                    <DialogContent>
                        <DialogHeader>
                            <DialogTitle>Connect a social account</DialogTitle>
                        </DialogHeader>
                        <div className="space-y-3">
                            <div>
                                <Label>Brand</Label>
                                <Select value={brandId} onValueChange={setBrandId}>
                                    <SelectTrigger data-testid={TESTIDS.accountsConnectBrandSelect}>
                                        <SelectValue placeholder="Choose a brand" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {brands.map((b) => (
                                            <SelectItem key={b.id} value={b.id}>
                                                {b.name}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                            <div>
                                <Label>Instagram handle</Label>
                                <Input
                                    value={handle}
                                    onChange={(e) => setHandle(e.target.value)}
                                    placeholder="yourhandle"
                                    data-testid={TESTIDS.accountsConnectHandleInput}
                                />
                            </div>
                            <div>
                                <Label>Display name (optional)</Label>
                                <Input value={displayName} onChange={(e) => setDisplayName(e.target.value)} />
                            </div>
                            <div className="rounded-md border border-dashed border-border bg-[hsl(var(--surface-2))] p-3 text-[11px] text-muted-foreground">
                                Meta App credentials are placeholders for now. This account record is stored as <span className="font-mono">connected_placeholder</span> until real OAuth tokens are exchanged.
                            </div>
                        </div>
                        <DialogFooter>
                            <Button onClick={create} disabled={busy} data-testid={TESTIDS.accountsConnectSubmit}>
                                {busy ? 'Connecting…' : 'Connect'}
                            </Button>
                        </DialogFooter>
                    </DialogContent>
                </Dialog>
            </div>

            <Card className="border-border bg-card">
                <div className="hidden border-b border-border px-4 py-2 text-[11px] font-medium uppercase tracking-wider text-muted-foreground md:grid md:grid-cols-[1fr_180px_140px_120px_80px]">
                    <div>Account</div>
                    <div>Brand</div>
                    <div>Status</div>
                    <div>Emergency stop</div>
                    <div className="text-right">Actions</div>
                </div>
                <ul className="divide-y divide-border">
                    {accounts.length === 0 && (
                        <li className="p-6 text-sm text-muted-foreground">No accounts connected yet.</li>
                    )}
                    {accounts.map((a) => {
                        const brand = brands.find((b) => b.id === a.brand_id);
                        const accent = brandColorVar(brand?.color_slot);
                        return (
                            <li
                                key={a.id}
                                className="grid grid-cols-1 items-center gap-3 px-4 py-3 md:grid-cols-[1fr_180px_140px_120px_80px]"
                                data-testid={TESTIDS.accountRow}
                                style={{ '--brand-accent-current': accent }}
                            >
                                <div className="flex items-center gap-3">
                                    <span
                                        className="inline-block h-2 w-2 shrink-0 rounded-full"
                                        style={{ backgroundColor: `hsl(${accent})` }}
                                    />
                                    <div>
                                        <div className="font-mono text-sm">@{a.handle}</div>
                                        <div className="text-[11px] text-muted-foreground">{a.display_name || a.platform}</div>
                                    </div>
                                </div>
                                <div className="text-sm text-muted-foreground">{brand?.name || '—'}</div>
                                <div>
                                    <span className="inline-flex items-center gap-1 rounded-md border border-border bg-[hsl(var(--surface-2))] px-2 py-0.5 text-[11px] text-muted-foreground">
                                        {a.status}
                                    </span>
                                </div>
                                <div className="flex items-center gap-2">
                                    <Switch
                                        checked={!!a.emergency_stop}
                                        onCheckedChange={() => toggleStop(a)}
                                        data-testid={TESTIDS.accountEmergencyStopToggle}
                                    />
                                    {a.emergency_stop ? (
                                        <span className="inline-flex items-center text-[11px] text-[hsl(var(--danger))]">
                                            <ShieldOff className="mr-1 h-3 w-3" /> stopped
                                        </span>
                                    ) : null}
                                </div>
                                <div className="text-right">
                                    <Button
                                        size="sm"
                                        variant="ghost"
                                        onClick={() => remove(a.id)}
                                        className="text-muted-foreground hover:text-foreground"
                                    >
                                        <Trash2 className="h-4 w-4" />
                                    </Button>
                                </div>
                            </li>
                        );
                    })}
                </ul>
            </Card>
        </div>
    );
}
